/**
 * Create Folder Dialog
 * Dialog for creating new folders in the media manager
 */

import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from '@mui/material'
import { CreateNewFolder } from '@mui/icons-material'
import { useCreateFolder } from '../hooks/useFiles'
import type { MediaFolderDto } from '@api/types/files'

interface CreateFolderDialogProps {
  open: boolean
  onClose: () => void
  parentFolderId?: string | null
  folders?: MediaFolderDto[]
}

const FOLDER_COLORS = [
  { value: '#FFC107', label: 'Amber' },
  { value: '#4CAF50', label: 'Green' },
  { value: '#2196F3', label: 'Blue' },
  { value: '#9C27B0', label: 'Purple' },
  { value: '#F44336', label: 'Red' },
  { value: '#FF9800', label: 'Orange' },
  { value: '#00BCD4', label: 'Cyan' },
  { value: '#795548', label: 'Brown' },
  { value: '#607D8B', label: 'Grey' },
]

export function CreateFolderDialog({
  open,
  onClose,
  parentFolderId,
  folders = [],
}: CreateFolderDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedParentId, setSelectedParentId] = useState<string | null>(parentFolderId ?? null)
  const [color, setColor] = useState('#FFC107')

  const createFolder = useCreateFolder()

  const handleSubmit = async () => {
    if (!name.trim()) return

    try {
      await createFolder.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
        parentFolderId: selectedParentId,
        color,
      })
      handleClose()
    } catch (error) {
      console.error('Failed to create folder:', error)
    }
  }

  const handleClose = () => {
    setName('')
    setDescription('')
    setSelectedParentId(parentFolderId ?? null)
    setColor('#FFC107')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CreateNewFolder />
        Create New Folder
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label="Folder Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
            placeholder="Enter folder name"
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Optional description"
          />

          <FormControl fullWidth>
            <InputLabel>Parent Folder</InputLabel>
            <Select
              value={selectedParentId ?? ''}
              onChange={(e) => setSelectedParentId(e.target.value || null)}
              label="Parent Folder"
            >
              <MenuItem value="">
                <em>Root (No Parent)</em>
              </MenuItem>
              {folders.map((folder) => (
                <MenuItem key={folder.id} value={folder.id}>
                  {folder.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Folder Color</InputLabel>
            <Select
              value={color}
              onChange={(e) => setColor(e.target.value)}
              label="Folder Color"
              renderValue={(selected) => {
                const colorOption = FOLDER_COLORS.find((c) => c.value === selected)
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 0.5,
                        bgcolor: selected,
                      }}
                    />
                    {colorOption?.label || 'Custom'}
                  </Box>
                )
              }}
            >
              {FOLDER_COLORS.map((colorOption) => (
                <MenuItem key={colorOption.value} value={colorOption.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 0.5,
                        bgcolor: colorOption.value,
                      }}
                    />
                    {colorOption.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createFolder.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!name.trim() || createFolder.isPending}
          startIcon={createFolder.isPending ? <CircularProgress size={16} /> : <CreateNewFolder />}
        >
          {createFolder.isPending ? 'Creating...' : 'Create Folder'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
